import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, X, Plus, Trash2, Settings, Users, Compass, Bell, Send, Eye, Check, Pin, PinOff
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, BACKEND_URL } from "../../lib/api";
import { ImageUploader } from "../ImageUploader";

export function AdminBlogManager() {
  const [articles, setArticles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [genModal, setGenModal] = useState(false);
  const [genMode, setGenMode] = useState('random'); // 'random' | 'theme'
  const [genTopic, setGenTopic] = useState('');

  const BLOG_THEMES = [
    "Voyance & tarot", "Pranic Healing", "Reiki", "Lithothérapie",
    "Chakras & ancrage", "Rituels lunaires", "Sorcellerie & wicca",
    "Numérologie", "Médiumnité", "Purification énergétique",
    "Cristaux & pierres", "Éveil spirituel"
  ];

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/blog/articles`, { params: { status: '', limit: 50 } });
      setArticles(res.data.articles || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = genMode === 'theme' && genTopic.trim() ? { count: 1, topic: genTopic.trim() } : { count: 1 };
      await axios.post(`${API}/blog/generate`, payload, { withCredentials: true });
      toast.success(genMode === 'theme'
        ? `Génération d'un article sur « ${genTopic.trim()} » lancée ! Patiente 1-2 minutes...`
        : "Génération d'un article aléatoire lancée ! Patiente 1-2 minutes...");
      setGenModal(false);
      setGenTopic('');
      setGenMode('random');
      setTimeout(fetchArticles, 60000);
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await axios.delete(`${API}/blog/articles/${articleId}`, { withCredentials: true });
      setArticles(prev => prev.filter(a => a.article_id !== articleId));
      toast.success("Article supprimé");
    } catch (error) { toast.error("Erreur"); }
  };

  const handleToggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/blog/articles/${article.article_id}`, {
        status: newStatus,
        published_at: newStatus === 'published' ? new Date().toISOString() : null
      }, { withCredentials: true });
      setArticles(prev => prev.map(a => a.article_id === article.article_id ? { ...a, status: newStatus } : a));
      toast.success(newStatus === 'published' ? "Article publié" : "Article mis en brouillon");
    } catch (error) { toast.error("Erreur"); }
  };

  const handleToggleFeatured = async (article) => {
    try {
      const res = await axios.put(`${API}/blog/articles/${article.article_id}/feature`, {}, { withCredentials: true });
      const newVal = res.data.is_featured;
      setArticles(prev => prev.map(a => a.article_id === article.article_id ? { ...a, is_featured: newVal } : a));
      toast.success(newVal ? "Article épinglé en haut du blog" : "Article retiré des épingles");
    } catch (error) { toast.error("Erreur"); }
  };

  const openEdit = async (article) => {
    setEditingArticle(article.article_id);
    // Le champ `content` n'est pas inclus dans la liste (optimisation API).
    // On recharge l'article complet via son slug avant d'ouvrir le formulaire.
    setEditForm({
      title: article.title || '',
      excerpt: article.excerpt || '',
      category: article.category || '',
      meta_title: article.meta_title || '',
      meta_description: article.meta_description || '',
      content: article.content || '',
      image_url: article.image_url || '',
    });
    try {
      const res = await axios.get(`${API}/blog/articles/${article.slug || article.article_id}?admin=1`);
      const full = res.data || {};
      setEditForm({
        title: full.title || '',
        excerpt: full.excerpt || '',
        category: full.category || '',
        meta_title: full.meta_title || '',
        meta_description: full.meta_description || '',
        content: full.content || '',
        image_url: full.image_url || '',
      });
    } catch (e) {
      toast.error("Impossible de charger le contenu complet de l'article");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/blog/articles/${editingArticle}`, editForm, { withCredentials: true });
      toast.success("Article mis à jour");
      setEditingArticle(null);
      fetchArticles();
    } catch (error) { toast.error("Erreur"); }
  };

  return (
    <Card className="bg-[#261B3B] border-white/10" data-testid="admin-blog-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D926A9]" />
            Blog ({articles.length} articles)
          </CardTitle>
          <Button onClick={() => setGenModal(true)} disabled={generating} className="gradient-primary" data-testid="open-generate-modal">
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Génération...' : 'Générer un article'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {editingArticle ? (
          <div className="space-y-3" data-testid="blog-edit-form">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-lg">Modifier l'article</h3>
              <button onClick={() => setEditingArticle(null)} className="text-[#B59ED1] text-sm hover:text-white">← Retour à la liste</button>
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Titre</label>
              <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="bg-[#1B132B] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Image de couverture</label>
              <ImageUploader
                currentUrl={editForm.image_url}
                onUploaded={(url) => setEditForm({...editForm, image_url: url})}
                category="blog"
                previewAspect="16/9"
                previewMaxWidth="480px"
              />
              <p className="text-[10px] text-[#B59ED1] mt-1">Formats acceptés : JPG, PNG, WebP, GIF — max 10 Mo. L'image est stockée durablement (résiste aux redéploiements).</p>
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Extrait (résumé court)</label>
              <Textarea value={editForm.excerpt} onChange={(e) => setEditForm({...editForm, excerpt: e.target.value})} className="bg-[#1B132B] border-white/10 min-h-[60px]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Meta title (SEO)</label>
                <Input value={editForm.meta_title} onChange={(e) => setEditForm({...editForm, meta_title: e.target.value})} className="bg-[#1B132B] border-white/10" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Catégorie</label>
                <Input value={editForm.category} onChange={(e) => setEditForm({...editForm, category: e.target.value})} className="bg-[#1B132B] border-white/10" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Meta description (SEO)</label>
              <Textarea value={editForm.meta_description} onChange={(e) => setEditForm({...editForm, meta_description: e.target.value})} className="bg-[#1B132B] border-white/10 min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Contenu complet (Markdown)</label>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                className="bg-[#1B132B] border-white/10 min-h-[400px] font-mono text-xs leading-relaxed"
                data-testid="blog-edit-content"
              />
              <p className="text-[10px] text-[#B59ED1] mt-1">Markdown supporté : # Titre · **gras** · *italique* · - liste · [lien](url)</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSaveEdit} className="gradient-primary" data-testid="blog-edit-save">Enregistrer les modifications</Button>
              <Button onClick={() => setEditingArticle(null)} variant="outline" className="border-white/10">Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {articles.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#B59ED1] mb-3">Aucun article. Lance la génération !</p>
              </div>
            ) : articles.map(a => (
              <div key={a.article_id} className="p-3 bg-[#1B132B] rounded-lg flex items-start gap-3" data-testid={`blog-row-${a.article_id}`}>
                {a.image_url && <img src={a.image_url.startsWith('/') ? `${BACKEND_URL}${a.image_url}` : a.image_url} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" onError={(e) => {e.target.style.display='none';}} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-medium text-sm truncate">{a.title}</h4>
                    {a.is_featured && <Badge className="text-[10px] bg-[#D926A9]/20 text-[#D926A9] flex items-center gap-1"><Pin className="w-3 h-3" />Épinglé</Badge>}
                    <Badge className={`text-[10px] ${a.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{a.status === 'published' ? 'Publié' : 'Brouillon'}</Badge>
                    <Badge className="text-[10px] bg-[#3A115E] text-[#E2D5F8]">{a.category}</Badge>
                  </div>
                  <p className="text-[#B59ED1] text-xs line-clamp-1">{a.excerpt}</p>
                  <span className="text-[10px] text-[#B59ED1]">{a.views || 0} vues • {a.published_at && new Date(a.published_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" className={a.is_featured ? "text-[#D926A9]" : "text-[#B59ED1]"} onClick={() => handleToggleFeatured(a)} data-testid={`feature-blog-${a.article_id}`} title={a.is_featured ? "Retirer l'épingle (cet article quittera le haut du blog)" : "Épingler en haut du blog (visible à la une pour tous les visiteurs)"}>{a.is_featured ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}</Button>
                  <Button variant="ghost" size="sm" className="text-[#00E5FF]" onClick={() => openEdit(a)} data-testid={`edit-blog-${a.article_id}`} title="Modifier"><Settings className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className={a.status === 'published' ? 'text-yellow-400' : 'text-green-400'} onClick={() => handleToggleStatus(a)} data-testid={`toggle-blog-${a.article_id}`} title={a.status === 'published' ? 'Mettre en brouillon' : 'Publier'}>{a.status === 'published' ? <Eye className="w-3 h-3" /> : <Check className="w-3 h-3" />}</Button>
                  <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete(a.article_id)} data-testid={`delete-blog-${a.article_id}`} title="Supprimer"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Generate Article Modal */}
      <AnimatePresence>
        {genModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !generating && setGenModal(false)} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="generate-modal">
              <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D926A9]" /> Générer un article</h3>
              <p className="text-sm text-[#B59ED1] mb-5">L'IA va rédiger un article SEO optimisé (~1500 mots) en tutoiement avec image.</p>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <button type="button" onClick={() => setGenMode('random')} className={`p-4 rounded-xl border-2 text-center transition-all ${genMode==='random'?'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="gen-mode-random">
                  <Sparkles className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold">Aléatoire</p>
                  <p className="text-[10px] opacity-75">Sujet choisi par l'IA</p>
                </button>
                <button type="button" onClick={() => setGenMode('theme')} className={`p-4 rounded-xl border-2 text-center transition-all ${genMode==='theme'?'border-[#D926A9] bg-[#D926A9]/10 text-[#D926A9]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="gen-mode-theme">
                  <Compass className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold">Thème choisi</p>
                  <p className="text-[10px] opacity-75">Tu imposes le sujet</p>
                </button>
              </div>

              {genMode === 'theme' && (
                <div className="space-y-3 mb-5">
                  <p className="text-xs text-[#B59ED1] uppercase tracking-wider">Suggestions de thèmes</p>
                  <div className="flex flex-wrap gap-2">
                    {BLOG_THEMES.map(t => (
                      <button key={t} type="button" onClick={() => setGenTopic(t)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${genTopic===t?'border-[#D926A9] bg-[#D926A9]/15 text-[#D926A9]':'border-white/15 bg-[#1B132B] text-[#E2D5F8] hover:border-[#D926A9]/40'}`}
                        data-testid={`gen-chip-${t.replace(/\W/g,'_')}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">
                      Ou saisis ton sujet personnalisé
                      <span className="text-[#B59ED1]/60 ml-1">({genTopic.length} / 2000 caractères)</span>
                    </label>
                    <Textarea
                      value={genTopic}
                      onChange={(e) => setGenTopic(e.target.value)}
                      placeholder="ex. comment utiliser l'améthyste pour favoriser le sommeil — tu peux écrire tout un brief : angle éditorial, ton souhaité, mots-clés à inclure, sections à aborder, etc."
                      className="bg-[#1B132B] border-white/10 min-h-[160px] resize-y font-sans text-sm leading-relaxed"
                      maxLength={2000}
                      rows={7}
                      data-testid="gen-topic-input"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={handleGenerate} disabled={generating || (genMode === 'theme' && !genTopic.trim())} className="gradient-primary flex-1" data-testid="gen-confirm">
                  <Sparkles className="w-4 h-4 mr-2" /> {generating ? 'Génération en cours...' : 'Lancer la génération'}
                </Button>
                <Button onClick={() => setGenModal(false)} disabled={generating} variant="outline" className="border-white/10">Annuler</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
